import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { addToWaitingList } from '../../services/firestore';

const initialState = {
  childFirstName: '',
  childLastName: '',
  childDOB: '',
  childGender: '',
  childExtraSupport: '',
  childExtraSupportExplain: '',
  parentFirstName: '',
  parentLastName: '',
  parentRelationship: '',
  parentEmail: '',
  parentEmailConfirm: '',
  parentPhone: '',
  parentAddress: '',
  enrollmentStartDate: '',
  enrollmentNotes: '',
  declarationAgreed: false
};

const WaitlistForm = ({ provider, onClose }) => {
  const { currentUser } = useAuth();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!form.declarationAgreed) {
        setError('You must agree to the declaration.');
        setSubmitting(false);
        return;
      }
      if (form.parentEmail !== form.parentEmailConfirm) {
        setError('Emails do not match.');
        setSubmitting(false);
        return;
      }
      // Compose data for Firestore
      const parentData = {
        parentUid: currentUser.uid,
        childData: {
          name: form.childFirstName + ' ' + form.childLastName,
          firstName: form.childFirstName,
          lastName: form.childLastName,
          dob: form.childDOB,
          gender: form.childGender,
          extraSupport: form.childExtraSupport,
          extraSupportExplain: form.childExtraSupportExplain
        },
        requestData: {
          parentFirstName: form.parentFirstName,
          parentLastName: form.parentLastName,
          relationship: form.parentRelationship,
          email: form.parentEmail,
          phone: form.parentPhone,
          address: form.parentAddress,
          enrollmentStartDate: form.enrollmentStartDate,
          enrollmentNotes: form.enrollmentNotes
        }
      };
      await addToWaitingList(provider.uid, parentData);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="waitlist-modal">
        <div className="waitlist-modal-content">
          <h2>Successfully joined the waitlist!</h2>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="waitlist-modal">
      <div className="waitlist-modal-content modern-waitlist-form">
        <h2 className="form-title">Join Waitlist for {provider.profile?.displayName || provider.displayName || 'Provider'}</h2>
        <form onSubmit={handleSubmit} className="waitlist-form">
          <div className="form-section">
            <h3 className="section-title">Child Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="childFirstName">Child's First Name</label>
                <input id="childFirstName" name="childFirstName" placeholder="First Name" value={form.childFirstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="childLastName">Child's Last Name</label>
                <input id="childLastName" name="childLastName" placeholder="Last Name" value={form.childLastName} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="childDOB">Child's Date of Birth</label>
                <input id="childDOB" name="childDOB" type="date" placeholder="Date of Birth" value={form.childDOB} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="childGender">Child's Gender</label>
                <input id="childGender" name="childGender" placeholder="Gender" value={form.childGender} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Does your child require extra support?</label>
                <div className="radio-group-inline">
                  <label><input type="radio" name="childExtraSupport" value="Yes" checked={form.childExtraSupport === 'Yes'} onChange={handleChange} /> Yes</label>
                  <label><input type="radio" name="childExtraSupport" value="No" checked={form.childExtraSupport === 'No'} onChange={handleChange} /> No</label>
                  <label><input type="radio" name="childExtraSupport" value="Unsure" checked={form.childExtraSupport === 'Unsure'} onChange={handleChange} /> Unsure</label>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="childExtraSupportExplain">If yes or unsure, please explain</label>
                <input id="childExtraSupportExplain" name="childExtraSupportExplain" placeholder="Explanation (optional)" value={form.childExtraSupportExplain} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Parent or Guardian</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="parentFirstName">First Name</label>
                <input id="parentFirstName" name="parentFirstName" placeholder="First Name" value={form.parentFirstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="parentLastName">Last Name</label>
                <input id="parentLastName" name="parentLastName" placeholder="Last Name" value={form.parentLastName} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="parentRelationship">Relationship to the child</label>
                <input id="parentRelationship" name="parentRelationship" placeholder="Relationship to the child" value={form.parentRelationship} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="parentPhone">Phone Number</label>
                <input id="parentPhone" name="parentPhone" placeholder="Phone Number" value={form.parentPhone} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="parentEmail">Email</label>
                <input id="parentEmail" name="parentEmail" placeholder="Email" value={form.parentEmail} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="parentEmailConfirm">Confirm Email</label>
                <input id="parentEmailConfirm" name="parentEmailConfirm" placeholder="Confirm Email" value={form.parentEmailConfirm} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="parentAddress">Address</label>
                <input id="parentAddress" name="parentAddress" placeholder="Address" value={form.parentAddress} onChange={handleChange} required />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Enrollment Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="enrollmentStartDate">Preferred Start Date (Optional)</label>
                <input id="enrollmentStartDate" name="enrollmentStartDate" type="date" placeholder="yyyy-mm-dd" value={form.enrollmentStartDate} onChange={handleChange} />
              </div>
              <div className="form-group full-width">
                <label htmlFor="enrollmentNotes">Additional Notes (subsidy needs, schedule, etc.)</label>
                <textarea id="enrollmentNotes" name="enrollmentNotes" placeholder="Additional Notes (subsidy needs, schedule, etc.)" value={form.enrollmentNotes} onChange={handleChange} rows={2} />
              </div>
            </div>
          </div>

          <div className="form-section form-actions-row">
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>Join Waitlist</button>
              <button type="button" className="btn btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
            </div>
            {error && <div className="error-message">{error}</div>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default WaitlistForm; 